/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { TentTypeV1 } from '@camp67/model';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';
import { FormattedString } from '../i18n/useMessages';
import Sprite from '../sprites/sprite';
import { GalleryItem } from '../constants/tents';

export interface AccommodationAmenity {
  title: string;
  description: string;
}

/** Gallery image - can be a string URL or an object with portrait flag */
export type GalleryImageItem = string | GalleryItem;

export interface AccommodationContentProps {
  /** Gallery images - can be string URLs or objects with portrait flag */
  galleryImages: GalleryImageItem[];
  galleryAlt: string;
  description: React.ReactNode;
  amenities: AccommodationAmenity[];
  tentType?: TentTypeV1;
  cta?: string;
  // Quick info for prominent display
  priceDisplay?: string;
  capacity?: number;
  highlights?: string[];
}

export const AccommodationContent: React.FC<AccommodationContentProps> = ({
  galleryImages,
  galleryAlt,
  description,
  amenities,
  tentType,
  cta,
  priceDisplay,
  capacity,
  highlights = ['Comfortable bedding', 'Daily housekeeping', 'Hot water'],
}) => {
  const handleBookingClick = useCallback(() => {
    if (isInquiryModeEnabled) {
      onOpenInquiry();
    } else {
      const params = new URLSearchParams();
      if (tentType) params.set('tentType', tentType);
      window.location.href = `/packages/reservation${params.toString() ? `?${params.toString()}` : ''}`;
    }
  }, [tentType]);

  const ctaText = cta ?? (isInquiryModeEnabled ? 'Inquire Now' : 'Book Now');

  return (
    <div className="space-y-10">
      {/* Quick Info Card - First Glance Information */}
      {(priceDisplay || capacity) && (
        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 md:p-8 border border-orange-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Price & Capacity */}
            <div className="flex flex-wrap items-center gap-6 md:gap-8">
              {priceDisplay && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold mb-1">
                    <FormattedString message="camp67-accommodation.starting-from" />
                  </p>
                  <p className="text-3xl md:text-4xl font-bold text-orange-800 font-serif">
                    {priceDisplay}
                  </p>
                  <p className="text-sm text-gray-600">
                    <FormattedString message="camp67-accommodation.per-night" />
                  </p>
                </div>
              )}

              {capacity && (
                <div className="pl-6 md:pl-8 border-l border-orange-200">
                  <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold mb-1">
                    <FormattedString message="camp67-accommodation.capacity" />
                  </p>
                  <p className="text-3xl md:text-4xl font-bold text-orange-800 font-serif">
                    {capacity}
                  </p>
                  <p className="text-sm text-gray-600">
                    {capacity === 1 ? (
                      <FormattedString message="camp67-accommodation.guest" />
                    ) : (
                      <FormattedString message="camp67-accommodation.guests" />
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Book Now CTA */}
            <button
              onClick={handleBookingClick}
              className="flex-shrink-0 w-full md:w-auto px-8 py-4 bg-red-200 hover:bg-red-300 text-white rounded-xl uppercase text-sm tracking-wider font-bold shadow-lg shadow-red-400/30 transition-all hover:scale-[1.02]"
            >
              {ctaText} &rarr;
            </button>
          </div>

          {/* Quick Highlights */}
          {highlights.length > 0 && (
            <div className="mt-6 pt-6 border-t border-orange-200">
              <div className="flex flex-wrap gap-3">
                {highlights.map((highlight, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-orange-700 text-sm font-medium rounded-full border border-orange-200"
                  >
                    <svg
                      className="w-4 h-4 text-orange-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gallery Section */}
      <section>
        <h2 className="text-xl font-bold font-serif text-gray-900 mb-5 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <FormattedString message="camp67-accommodation.gallery" />
        </h2>
        <Gallery images={galleryImages} alt={galleryAlt} />
      </section>

      {/* Description */}
      <section>
        <h2 className="text-xl font-bold font-serif text-gray-900 mb-5 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <FormattedString message="camp67-accommodation.about" />
        </h2>
        <div className="prose prose-lg max-w-none text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-strong:text-gray-900">
          {description}
        </div>
      </section>

      {/* Amenities - What's Included */}
      <section>
        <h2 className="text-xl font-bold font-serif text-gray-900 mb-5 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          <FormattedString message="camp67-accommodation.whats-included" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {amenities.map((amenity, index) => (
            <AmenityCard
              key={index}
              title={amenity.title}
              description={amenity.description}
              open={index < 2}
            />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-red-200 rounded-2xl p-8 text-center">
        <h3 className="text-2xl font-bold text-white font-serif mb-3">
          <FormattedString message="camp67-accommodation.ready-cta-title" />
        </h3>
        <p className="text-white/90 mb-6 max-w-lg mx-auto">
          <FormattedString message="camp67-accommodation.ready-cta-description" />
        </p>
        <button
          onClick={handleBookingClick}
          className="px-10 py-4 bg-white hover:bg-gray-50 text-orange-600 rounded-xl uppercase text-sm tracking-wider font-bold shadow-lg transition-all hover:scale-[1.02]"
        >
          {ctaText} &rarr;
        </button>
      </section>
    </div>
  );
};

// Gallery component
interface GalleryProps {
  images: GalleryImageItem[];
  alt: string;
}

/** Helper to get imageUrl from a GalleryImageItem */
const getImageUrl = (item: GalleryImageItem): string =>
  typeof item === 'string' ? item : item.imageUrl;

/** Helper to check if an image is portrait */
const isPortrait = (item: GalleryImageItem): boolean =>
  typeof item === 'string' ? false : item.portrait === true;

const Gallery: React.FC<GalleryProps> = ({ images, alt }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = images[activeIndex];
  const activeIsPortrait = isPortrait(activeImage);

  return (
    <div className="space-y-4">
      {/* Main Image - fixed height to prevent jumping on mobile */}
      <div
        className={clsx(
          'relative w-full h-72 sm:h-96 md:h-[480px] overflow-hidden rounded-lg',
          activeIsPortrait ? 'bg-gray-100' : '',
        )}
      >
        <img
          src={getImageUrl(activeImage)}
          alt={`${alt} - ${activeIndex + 1} of ${images.length}`}
          className={clsx(
            'w-full h-full',
            activeIsPortrait ? 'object-contain' : 'object-cover',
          )}
        />
        {images.length > 1 && (
          <>
            <button
              onClick={() =>
                setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
              }
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-colors"
            >
              <Sprite
                name="prev"
                width="12"
                height="21"
                strokeWidth="3"
                className="w-3"
              />
            </button>
            <button
              onClick={() =>
                setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-colors"
            >
              <Sprite
                name="next"
                width="12"
                height="21"
                strokeWidth="3"
                className="w-3"
              />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={clsx(
                'relative aspect-video overflow-hidden rounded-md border-2 transition-all',
                activeIndex === index
                  ? 'border-orange-500 ring-2 ring-orange-200'
                  : 'border-gray-200 hover:border-orange-400',
              )}
            >
              <img
                src={getImageUrl(image)}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Amenity card component for grid layout
interface AmenityCardProps {
  title: string;
  description: string;
  open?: boolean;
}

const AmenityCard: React.FC<AmenityCardProps> = ({
  title,
  description,
  open = false,
}) => {
  const [isOpen, setIsOpen] = useState(open);

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);

  return (
    <details
      className="bg-white hover:bg-orange-50 rounded-lg border border-gray-200 hover:border-orange-200 transition-all duration-200 overflow-hidden cursor-pointer"
      open={isOpen}
      onClick={(e) => {
        e.preventDefault();
        toggleOpen();
      }}
    >
      <summary
        className={clsx(
          'p-4 cursor-pointer select-none font-sans uppercase tracking-wider font-medium flex flex-row items-center justify-between text-sm',
          {
            'pb-2': isOpen,
          },
        )}
      >
        <span className="text-gray-900">{title}</span>
        <svg
          className={clsx('w-5 h-5 stroke-gray-700 transition-transform duration-200', {
            'rotate-180': isOpen,
          })}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </summary>
      <div className="px-4 pb-4 text-sm border-t border-gray-100">
        <div className="pt-3 leading-relaxed text-gray-700">{description}</div>
      </div>
    </details>
  );
};

// Standard amenities for Camp67 accommodations
export const CAMP67_STANDARD_AMENITIES: AccommodationAmenity[] = [
  {
    title: 'Premium Accommodation',
    description:
      'Comfortable stay with quality bedding, pillows, blankets, and daily housekeeping service.',
  },
  {
    title: 'Three Daily Meals',
    description:
      'Nutritious vegetarian breakfast, lunch, and dinner prepared with fresh ingredients.',
  },
];

// Highlight presets
export const TWIN_ROOM_HIGHLIGHTS = [
  'Private room for 2',
  'Attached bathroom',
  'Hot water',
  'All meals included',
  'Daily housekeeping',
];

export const FAMILY_ROOM_HIGHLIGHTS = [
  'Private room for 4',
  'Attached bathroom',
  'Hot water',
  'All meals included',
  'Daily housekeeping',
];

export const DORM_HIGHLIGHTS = [
  'Comfortable single bed',
  'Shared bathroom',
  'Hot water',
  'All meals included',
  'Community experience',
];

export const PRIVATE_GROUP_HIGHLIGHTS = [
  'Private cottage for 8',
  'Attached bathroom',
  'Hot water',
  'All meals included',
  'Great for groups',
];
